import { AppError, ErrorCodes, ErrorMessages, formatErrorForUser } from "@shared/errorTypes";

export class ApiError extends Error {
  public appError: AppError;
  public statusCode: number;

  constructor(appError: AppError, statusCode: number = 500) {
    super(appError.message);
    this.appError = appError;
    this.statusCode = statusCode;
    this.name = 'ApiError';
  }
}

export function parseErrorResponse(response: Response, responseText: string): AppError {
  try {
    const errorData = JSON.parse(responseText);
    
    // If it's already an AppError structure
    if (errorData.code && errorData.message) {
      return errorData as AppError;
    }
    
    // Handle common HTTP status codes
    if (response.status === 401) {
      // Check for session timeout specifically
      if (errorData.sessionTimeout === true) {
        return {
          code: 'SESSION_TIMEOUT',
          message: "Your session has expired due to inactivity",
          resolution: "Please sign in again to continue",
          sessionTimeout: true
        } as AppError;
      }
      return ErrorMessages[ErrorCodes.UNAUTHORIZED];
    }
    
    if (response.status === 403) {
      // Preserve EMAIL_NOT_VERIFIED errors for proper handling
      if (errorData.error === 'EMAIL_NOT_VERIFIED') {
        return {
          code: 'EMAIL_NOT_VERIFIED',
          message: errorData.message || 'Please verify your email address before signing in',
          error: errorData.error,
          userId: errorData.userId,
          email: errorData.email,
          requiresVerification: errorData.requiresVerification || true
        } as AppError;
      }
      return ErrorMessages[ErrorCodes.FORBIDDEN];
    }
    
    if (response.status === 404) {
      // Try to determine what wasn't found based on the URL
      const url = response.url;
      if (url.includes('/users/')) return ErrorMessages[ErrorCodes.USER_NOT_FOUND];
      if (url.includes('/customers/')) return ErrorMessages[ErrorCodes.CUSTOMER_NOT_FOUND];
      if (url.includes('/assessments/')) return ErrorMessages[ErrorCodes.ASSESSMENT_NOT_FOUND];
      if (url.includes('/qbrs/')) return ErrorMessages[ErrorCodes.QBR_NOT_FOUND];
      if (url.includes('/templates/')) return ErrorMessages[ErrorCodes.TEMPLATE_NOT_FOUND];
      
      return {
        code: ErrorCodes.FILE_NOT_FOUND,
        message: "The requested resource was not found",
        resolution: "Please check that the item still exists and try again"
      };
    }
    
    if (response.status === 429) {
      return {
        code: ErrorCodes.TIMEOUT_ERROR,
        message: "Too many requests",
        resolution: "Please wait a moment before trying again"
      };
    }
    
    if (response.status >= 500) {
      return {
        code: ErrorCodes.INTERNAL_ERROR,
        message: errorData.message || "Internal server error",
        resolution: "Please try again. If the problem persists, contact technical support"
      };
    }
    
    // Default error with server message
    return {
      code: ErrorCodes.VALIDATION_ERROR,
      message: errorData.message || "An error occurred",
      resolution: "Please check your input and try again"
    };
    
  } catch (parseError) {
    // If we can't parse the response, create a generic error
    if (response.status >= 500) {
      return ErrorMessages[ErrorCodes.INTERNAL_ERROR];
    }
    
    return {
      code: ErrorCodes.NETWORK_ERROR,
      message: responseText || response.statusText || "Network error occurred",
      resolution: "Please check your connection and try again"
    };
  }
}

export function getFieldErrorMessage(field: string, errorType: string): AppError {
  const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
  
  switch (errorType) {
    case 'required':
      return {
        code: ErrorCodes.REQUIRED_FIELD,
        message: `${fieldName} is required`,
        resolution: "Please provide a value for this field",
        field
      };
    case 'email':
      return {
        code: ErrorCodes.INVALID_EMAIL,
        message: `Please enter a valid email address`,
        resolution: "Use the format: name@company.com",
        field
      };
    case 'minLength':
      return {
        code: ErrorCodes.VALUE_TOO_SHORT,
        message: `${fieldName} is too short`,
        resolution: "Please provide a longer value",
        field
      };
    case 'maxLength':
      return {
        code: ErrorCodes.VALUE_TOO_LONG,
        message: `${fieldName} is too long`,
        resolution: "Please shorten your input",
        field
      };
    default:
      return {
        code: ErrorCodes.VALIDATION_ERROR,
        message: `${fieldName} is invalid`,
        resolution: "Please check the value and try again",
        field
      };
  }
}

export function formatUserFriendlyError(error: unknown): string {
  if (error instanceof ApiError) {
    return formatErrorForUser(error.appError);
  }
  
  // Handle plain object errors thrown by mutations (e.g., login)
  if (error && typeof error === 'object' && 'appError' in error) {
    const appError = (error as { appError: any }).appError;
    // If appError has a message or error field, use it
    if (appError?.message) {
      return appError.message;
    }
    if (appError?.error) {
      return appError.error;
    }
  }
  
  if (error instanceof Error) {
    // Try to extract meaningful information from the error message
    const message = error.message;
    
    if (message.includes('Network Error') || message.includes('fetch')) {
      return formatErrorForUser(ErrorMessages[ErrorCodes.NETWORK_ERROR]);
    }
    
    if (message.includes('401')) {
      return formatErrorForUser(ErrorMessages[ErrorCodes.UNAUTHORIZED]);
    }
    
    if (message.includes('403')) {
      return formatErrorForUser(ErrorMessages[ErrorCodes.FORBIDDEN]);
    }
    
    if (message.includes('404')) {
      return formatErrorForUser(ErrorMessages[ErrorCodes.FILE_NOT_FOUND]);
    }
    
    if (message.includes('timeout')) {
      return formatErrorForUser(ErrorMessages[ErrorCodes.TIMEOUT_ERROR]);
    }
    
    // Return the original message if we can't categorize it
    return message;
  }
  
  return formatErrorForUser(ErrorMessages[ErrorCodes.INTERNAL_ERROR]);
}

// Hook for handling common error scenarios
export function useErrorHandler() {
  const handleError = (error: unknown, context?: string) => {
    const userMessage = formatUserFriendlyError(error);
    console.error(`Error in ${context || 'unknown context'}:`, error);
    return userMessage;
  };
  
  return { handleError };
}

// Validation helpers
export function validateEmail(email: string): AppError | null {
  if (!email) {
    return getFieldErrorMessage('email', 'required');
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return getFieldErrorMessage('email', 'email');
  }
  
  return null;
}

export function validateRequired(value: string | undefined | null, fieldName: string): AppError | null {
  if (!value || value.trim() === '') {
    return getFieldErrorMessage(fieldName, 'required');
  }
  return null;
}

export function validateLength(value: string, fieldName: string, min?: number, max?: number): AppError | null {
  if (min && value.length < min) {
    return {
      code: ErrorCodes.VALUE_TOO_SHORT,
      message: `${fieldName} must be at least ${min} characters`,
      resolution: `Please provide at least ${min} characters`,
      field: fieldName
    };
  }
  
  if (max && value.length > max) {
    return {
      code: ErrorCodes.VALUE_TOO_LONG,
      message: `${fieldName} must be no more than ${max} characters`,
      resolution: `Please keep it under ${max} characters`,
      field: fieldName
    };
  }
  
  return null;
}

export function validatePassword(password: string): AppError | null {
  if (!password) {
    return getFieldErrorMessage('password', 'required');
  }
  
  if (password.length < 8) {
    return {
      code: ErrorCodes.WEAK_PASSWORD,
      message: "Password must be at least 8 characters long",
      resolution: "Use at least 8 characters with uppercase, lowercase, numbers, and special characters",
      field: 'password'
    };
  }
  
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  if (!hasUpper || !hasLower || !hasNumber || !hasSpecial) {
    return {
      code: ErrorCodes.WEAK_PASSWORD,
      message: "Password must contain uppercase, lowercase, numbers, and special characters",
      resolution: "Include at least one uppercase letter, lowercase letter, number, and special character",
      field: 'password'
    };
  }
  
  return null;
}